/**
 * 1.4 P28: owner API access — keys (create shown once, revoke, usage),
 * effective limits, docs link. Admins see the same through the owner scope
 * and can set the per-owner limit override.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Switch, Platform, ActivityIndicator, Linking } from 'react-native';
import { Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../api/client';
import { useOwnerScope } from '../context/OwnerScopeContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../components/Toast';
import ContentContainer from '../components/ContentContainer';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Picker from '../components/Picker';
import InlineConfirm from '../components/InlineConfirm';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';

interface Key { id: string; name: string; prefix: string; scopes: string[]; createdAt: string; lastUsedAt: string | null; lastUsedIp: string | null; expiresAt: string | null; revokedAt: string | null; createdByRole: string | null }
interface Limits { effective: { perMinute: number; perDay: number }; override: { perMinute?: number; perDay?: number } | null; defaults: { perMinute: number; perDay: number } }

const DOCS_URL = 'https://docs.rinsepay.app/api';

export default function ApiAccessScreen({ adminOwnerId }: { adminOwnerId?: string }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const toast = useToast();
  const scope = useOwnerScope();
  const [keys, setKeys] = useState<Key[]>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [usage, setUsage] = useState<Record<string, { day: string; calls: number }[]>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [pii, setPii] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [perMinute, setPerMinute] = useState('');
  const [perDay, setPerDay] = useState('');

  const load = useCallback(async () => {
    try {
      const [k, l] = await Promise.all([scope.api.get<{ keys: Key[] }>('/api/owner/api-keys'), scope.api.get<Limits>('/api/owner/api-limits')]);
      setKeys(k.keys); setLimits(l);
      setPerMinute(l.override?.perMinute ? String(l.override.perMinute) : ''); setPerDay(l.override?.perDay ? String(l.override.perDay) : '');
      const live = k.keys.filter(x => !x.revokedAt).slice(0, 10);
      const u: Record<string, { day: string; calls: number }[]> = {};
      await Promise.all(live.map(async x => { try { u[x.id] = (await scope.api.get<{ days: { day: string; calls: number }[] }>(`/api/owner/api-keys/${x.id}/usage`)).days; } catch { /* skip */ } }));
      setUsage(u);
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not load API access', message: err.message }); }
    finally { setLoading(false); }
  }, [scope.api, toast]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.show({ type: 'error', title: 'Name the key (where it will be used)' }); return; }
    setBusy(true);
    try {
      const r = await scope.api.post<Key & { key: string }>('/api/owner/api-keys', { name: name.trim(), expiresInDays: expiry ? Number(expiry) : null, includePii: pii });
      setNewKey({ key: r.key, name: r.name }); setCreating(false); setName(''); setExpiry(''); setPii(false); setCopied(false);
      load();
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not create the key', message: err.message }); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!newKey) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(newKey.key);
      else { const Clipboard = (await import('@react-native-clipboard/clipboard')).default; Clipboard.setString(newKey.key); }
      setCopied(true);
    } catch { toast.show({ type: 'error', title: 'Copy failed — select the key and copy it manually' }); }
  };
  const revoke = async (id: string) => {
    setConfirmRevoke(null);
    try { await scope.api.delete(`/api/owner/api-keys/${id}`); toast.show({ type: 'success', title: 'Key revoked' }); load(); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not revoke', message: err.message }); }
  };
  const saveLimits = async () => {
    if (!adminOwnerId) return;
    setBusy(true);
    try {
      await api(`/api/admin/owners/${adminOwnerId}/api-limits`, { method: 'PUT', body: { perMinute: perMinute ? parseInt(perMinute) : null, perDay: perDay ? parseInt(perDay) : null } });
      toast.show({ type: 'success', title: 'Limits saved' }); load();
    } catch (err: any) { toast.show({ type: 'error', title: 'Could not save limits', message: err.message }); }
    finally { setBusy(false); }
  };

  const Spark = ({ days }: { days: { day: string; calls: number }[] }) => {
    const max = Math.max(1, ...days.map(d => d.calls));
    return (
      <View style={styles.spark}>
        {days.map(d => <View key={d.day} style={[styles.sparkBar, { height: 4 + Math.round((d.calls / max) * 20), backgroundColor: d.calls ? colors.primary : colors.borderLight }]} accessibilityLabel={`${d.day}: ${d.calls} calls`} />)}
        <Text style={styles.sparkText}>{days.reduce((a, d) => a + d.calls, 0)} calls / 7d</Text>
      </View>
    );
  };

  if (loading) return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  return (
    <>
      <Stack.Screen options={{ title: 'API Access' }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={900}>
          <Card style={styles.card}>
            <Text style={styles.title}>Owner API</Text>
            <Text style={styles.help}>Read everything in your account (locations, bays, controllers, sessions, transactions, refunds, customers, memberships, analytics) from your own software. Every request to /api/v1 needs one of the keys below as a Bearer token. Limits: {limits?.effective.perMinute}/minute and {limits?.effective.perDay}/day per key; heavy exports count 5.</Text>
            <TouchableOpacity onPress={() => Linking.openURL(DOCS_URL)}><Text style={styles.link}>Read the API docs →</Text></TouchableOpacity>
          </Card>

          {newKey && (
            <Card style={[styles.card, styles.newKeyCard]}>
              <Text style={styles.title}>Copy your key now</Text>
              <Text style={styles.help}>"{newKey.name}" is shown once. We only store a hash; if you lose it, revoke it and create another.</Text>
              <Text selectable style={styles.keyText}>{newKey.key}</Text>
              <View style={styles.row2}>
                <Button title={copied ? 'Copied' : 'Copy key'} size="sm" onPress={copy} icon={<Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={16} color="#fff" />} />
                <Button title="Done" size="sm" variant="outline" onPress={() => setNewKey(null)} />
              </View>
            </Card>
          )}

          <Card style={styles.card}>
            <View style={styles.headRow}>
              <Text style={styles.title}>Keys</Text>
              <Button title="Create key" size="sm" onPress={() => setCreating(v => !v)} icon={<Ionicons name="key-outline" size={16} color="#fff" />} />
            </View>
            {creating && (
              <View style={styles.createBox}>
                <Input label="Name (where it is used)" value={name} onChangeText={setName} placeholder="e.g. Zapier, accounting sync" maxLength={60} />
                <Picker label="Expires" value={expiry} onValueChange={setExpiry} options={[{ value: '', label: 'Never' }, { value: '30', label: 'In 30 days' }, { value: '90', label: 'In 90 days' }, { value: '365', label: 'In 1 year' }]} />
                <View style={styles.switchRow}>
                  <View style={{ flex: 1 }}><Text style={styles.switchLabel}>Include customer contact details (read:pii)</Text><Text style={styles.help}>Names, emails, phones and plates on customer and membership endpoints. Leave off unless the integration needs them.</Text></View>
                  <Switch value={pii} onValueChange={setPii} trackColor={{ false: colors.borderLight, true: colors.primary + '80' }} thumbColor={pii ? colors.primary : colors.textLight} />
                </View>
                <View style={styles.row2}>
                  <Button title="Create" size="sm" onPress={create} loading={busy} />
                  <Button title="Cancel" size="sm" variant="outline" onPress={() => setCreating(false)} />
                </View>
              </View>
            )}
            {keys.length === 0 && <Text style={styles.help}>No keys yet.</Text>}
            {keys.map(k => (
              <View key={k.id} style={[styles.keyRow, !!k.revokedAt && { opacity: 0.55 }]}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.keyName}>{k.name} <Text style={styles.prefix}>rp_…_{k.prefix}…</Text>{k.createdByRole === 'admin' ? <Text style={styles.prefix}> · created by Rinsepay support</Text> : null}</Text>
                  <Text style={styles.meta}>{k.scopes.join(', ')} · created {new Date(k.createdAt).toLocaleDateString()} · {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleString()}${k.lastUsedIp ? ` from ${k.lastUsedIp}` : ''}` : 'never used'}{k.expiresAt ? ` · expires ${new Date(k.expiresAt).toLocaleDateString()}` : ''}{k.revokedAt ? ` · revoked ${new Date(k.revokedAt).toLocaleDateString()}` : ''}</Text>
                  {!k.revokedAt && usage[k.id] && <Spark days={usage[k.id]} />}
                  {confirmRevoke === k.id && <InlineConfirm visible message={`Revoke "${k.name}"? Anything using it stops working immediately.`} confirmLabel="Revoke" onConfirm={() => revoke(k.id)} onCancel={() => setConfirmRevoke(null)} />}
                </View>
                {!k.revokedAt && (
                  <TouchableOpacity onPress={() => setConfirmRevoke(k.id)} hitSlop={8} accessibilityLabel={`Revoke ${k.name}`}><Ionicons name="trash-outline" size={20} color={colors.danger} /></TouchableOpacity>
                )}
              </View>
            ))}
          </Card>

          {adminOwnerId && limits && (
            <Card style={styles.card}>
              <Text style={styles.title}>Rate limit override (admin)</Text>
              <Text style={styles.help}>Defaults {limits.defaults.perMinute}/min and {limits.defaults.perDay}/day. Blank = default.</Text>
              <View style={styles.row2}>
                <View style={{ flex: 1 }}><Input label="Per minute" value={perMinute} onChangeText={v => setPerMinute(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" placeholder={String(limits.defaults.perMinute)} /></View>
                <View style={{ flex: 1 }}><Input label="Per day" value={perDay} onChangeText={v => setPerDay(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" placeholder={String(limits.defaults.perDay)} /></View>
              </View>
              <Button title="Save limits" size="sm" onPress={saveLimits} loading={busy} style={{ alignSelf: 'flex-start' }} />
            </Card>
          )}
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  card: { marginBottom: spacing.md },
  newKeyCard: { borderWidth: 1, borderColor: c.primary },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  help: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm, lineHeight: 18 },
  link: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.semibold },
  keyText: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: fontSize.sm, color: c.text, padding: spacing.sm, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md, marginBottom: spacing.sm },
  row2: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center' },
  headRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  createBox: { padding: spacing.sm, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, marginBottom: spacing.sm },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingVertical: spacing.xs },
  switchLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  keyRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  keyName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  prefix: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.normal },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  spark: { flexDirection: 'row', alignItems: 'flex-end', gap: 3, marginTop: 6, height: 26 },
  sparkBar: { width: 10, borderRadius: 2 },
  sparkText: { fontSize: 10, color: c.textSecondary, marginLeft: 6 },
});
